import React from "react";
import { Box, Flex, HStack, Input, Select, Text } from "@chakra-ui/react";
import { useCustomTheme } from "../../../contexts/CustomThemeContext";

export type ToolbarSelectOption = {
    value: string;
    label: string;
};

type ToolbarSelectConfig = {
    value: string;
    onChange: (value: string) => void;
    options: ToolbarSelectOption[];
    maxW?: any;
};

type AdminToolbarProps = {
    search?: {
        value: string;
        onChange: (value: string) => void;
        placeholder?: string;
        maxW?: any;
    };
    selects?: ToolbarSelectConfig[];
    rightInfo?: React.ReactNode;
    accentGradient?: string;
};

export const AdminToolbar: React.FC<AdminToolbarProps> = ({
    search,
    selects = [],
    rightInfo,
    accentGradient,
}) => {
    const { theme } = useCustomTheme();
    const isDark = theme.name === "dark";

    const brand = theme.brand || {
        electricBlue: "#27b7ff",
    };

    const softShadow = theme.shadowSm;
    const cardBg = theme.surface;
    const textColor = theme.text;
    const cardsBorderRadii ="8px";

    const baseControlStyle = {
        border: `1px solid ${theme.border}`,
        borderRadius: {cardsBorderRadii},
        boxShadow: softShadow,
        bg: isDark ? "rgba(255,255,255,0.035)" : "rgba(255,255,255,0.92)",
        color: textColor,
        _hover: {
            borderColor: brand.electricBlue,
            bg: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,1)",
        },
        _focus: {
            borderColor: brand.electricBlue,
            boxShadow: `0 0 0 1px ${brand.electricBlue}`,
        },
    };

    return (
        <Box
            borderRadius="16px"
            bg={cardBg}
            boxShadow={softShadow}
            border={`1px solid ${theme.border}`}
            p={{ base: 3, md: 4 }}
            mb={3}
            position="relative"
            overflow="hidden"
            _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "3px",
                bg: accentGradient || brand.electricBlue,
            }}
        >
            <Flex gap={3} wrap="wrap" align="center" direction={{ base: "column", md: "row" }}>
                {search && (
                    <Input
                        placeholder={search.placeholder || "Search..."}
                        value={search.value}
                        onChange={(e) => search.onChange(e.target.value)}
                        maxW={search.maxW || { base: "100%", md: "360px" }}
                        {...baseControlStyle}
                    />
                )}

                {selects.map((s, idx) => (
                    <Select
                        key={`${idx}-${s.value}`}
                        value={s.value}
                        onChange={(e) => s.onChange(e.target.value)}
                        maxW={s.maxW || { base: "100%", md: "180px" }}
                        {...baseControlStyle}
                        sx={{
                            option: {
                                background: isDark ? "#1f2430" : "#ffffff",
                                color: isDark ? "#f7fafc" : "#1a202c",
                            },
                        }}
                    >
                        {s.options.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                                {opt.label}
                            </option>
                        ))}
                    </Select>
                ))}

                {rightInfo && (
                    <HStack
                        ml={{ md: "auto" }}
                        spacing={2}
                        px={3}
                        py={2}
                        borderRadius="12px"
                        border={`1px solid ${theme.border}`}
                        bg={isDark ? "rgba(255,255,255,0.02)" : theme.surfaceAlt}
                        boxShadow={softShadow}
                        w={{ base: "100%", md: "auto" }}
                    >
                        <Box w="7px" h="7px" borderRadius="full" bg={theme.brand?.indigo || "#6f7cff"} />
                        <Text color={theme.textMuted} fontSize="sm" fontWeight="600">
                            {rightInfo}
                        </Text>
                    </HStack>
                )}
            </Flex>
        </Box>
    );
};